import React, { useState } from 'react';
import { Button } from '../../../shadcn/components/ui/button';
import { useAppStore } from 'store/appStore';
import { exportToCsv } from '../utils/csvExport';
import { ExportMeetingReportItem } from 'api-types';
import { getDuration } from './CallReport';
import { FloatingAlert } from '../../../components/ErrorHandling';

interface ExportMeetingReportsButtonProps {
  meetingId?: string;
}

export const ExportMeetingReportsButton: React.FC<ExportMeetingReportsButtonProps> = ({
  meetingId,
}) => {
  const exportMeetingReports = useAppStore(state => state.exportMeetingReports);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatReport = (report: ExportMeetingReportItem) => {
    const criteriaFeedback = JSON.parse(report.criteriaFeedback || '[]');
    const flattenedCriteria = criteriaFeedback.reduce((acc: any, item: any, index: number) => {
      return {
        ...acc,
        [`Criteria${index + 1}`]: item.criteria || '',
        [`Feedback${index + 1}`]: item.feedback || '',
        [`Score${index + 1}`]: item.qualificationScore || '',
      };
    }, {});

    return {
      MeetingID: report.meetingId || '',
      AssessmentID: report.id || '',
      Name: `${report.firstName || ''} ${report.lastName || ''}`.trim(),
      Email: report.email || '',
      Phone: report.phone || '',
      WhatsApp: report.whatsapp || '',
      StartTime: report.startedAt || '',
      Duration:
        report.endedAt && report.startedAt ? getDuration(report.startedAt, report.endedAt) : '',
      OverallFeedback: report.overallFeedback || '',
      CandidateInformation: report.candidateInformation || '',
      ...flattenedCriteria,
    };
  };

  const handleExportMeetingReports = async () => {
    if (!meetingId) return;
    setExporting(true);
    setError(null);
    try {
      const response = await exportMeetingReports({ meetingId });
      const csvData = (response.reports ?? []).map(formatReport);
      exportToCsv(`meeting-${meetingId}-reports.csv`, csvData);
    } catch (err) {
      setError('Failed to export meeting reports.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="text-base font-medium"
        disabled={!meetingId || exporting}
        onClick={handleExportMeetingReports}
      >
        {exporting ? 'Exporting...' : 'Export CSV'}
      </Button>
      {error && (
        <FloatingAlert
          message={error}
          title="Export Failed"
          onClose={() => setError(null)}
          autoClose={true}
          isVisible={!!error}
          variant="destructive"
        />
      )}
    </>
  );
};
